import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Project } from '../types';

interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectModal = ({ project, isOpen, onClose }: ProjectModalProps) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const slides = project?.screenshots && project.screenshots.length > 0
    ? project.screenshots
    : project?.image
      ? [project.image]
      : [];

  const totalSlides = slides.length;

  const goTo = useCallback((index: number) => {
    setIsImageLoading(true);
    setCurrentSlide((index + totalSlides) % totalSlides);
  }, [totalSlides]);

  const prev = useCallback(() => goTo(currentSlide - 1), [currentSlide, goTo]);
  const next = useCallback(() => goTo(currentSlide + 1), [currentSlide, goTo]);

  // Reset slide when project changes
  useEffect(() => {
    setCurrentSlide(0);
    setIsImageLoading(true);
  }, [project?.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, prev, next]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-[#020024]/95 backdrop-blur-lg"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[95vh] glass-card rounded-2xl shadow-brand-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 p-2 glass border border-white/15 rounded-full hover:border-accent/50 hover:bg-white/10 transition-all duration-300"
          aria-label="Close modal"
        >
          <Icon name="X" size={20} color="white" />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">

          {/* ── IMAGE SLIDER ─────────────────────────────────────────── */}
          {totalSlides > 0 && (
            <div className="relative bg-[#020024] select-none" style={{ height: '360px' }}>

              {/* Main image */}
              <div className="relative w-full h-full overflow-hidden">
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#020024]">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <img
                  key={slides[currentSlide]}
                  src={slides[currentSlide]}
                  alt={`${project.title} screenshot ${currentSlide + 1}`}
                  className="w-full h-full object-contain transition-opacity duration-300"
                  style={{ opacity: isImageLoading ? 0 : 1 }}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                  draggable={false}
                  onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
                  onTouchEnd={(e) => {
                    if (touchStart === null) return;
                    const delta = touchStart - e.changedTouches[0].clientX;
                    if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
                    setTouchStart(null);
                  }}
                />
                {/* Bottom gradient */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#020024] to-transparent pointer-events-none" />
              </div>

              {/* Prev / Next arrows — only show when multiple slides */}
              {totalSlides > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2.5 glass border border-white/15 rounded-full hover:border-accent/50 hover:bg-accent/10 transition-all duration-300 group"
                    aria-label="Previous screenshot"
                  >
                    <Icon name="ChevronLeft" size={20} color="white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2.5 glass border border-white/15 rounded-full hover:border-accent/50 hover:bg-accent/10 transition-all duration-300 group"
                    aria-label="Next screenshot"
                  >
                    <Icon name="ChevronRight" size={20} color="white" />
                  </button>
                </>
              )}

              {/* Slide counter badge */}
              {totalSlides > 1 && (
                <div className="absolute top-3 left-3 z-10 glass border border-white/15 px-3 py-1 rounded-full text-xs font-medium text-white/80">
                  {currentSlide + 1} / {totalSlides}
                </div>
              )}

              {/* Featured badge */}
              {project.featured && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full gradient-cta text-white text-xs font-semibold shadow-brand">
                  <Icon name="Star" size={13} color="currentColor" />
                  {t('portfolio.featuredWork')}
                </div>
              )}

              {/* Dot indicators */}
              {totalSlides > 1 && totalSlides <= 20 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); goTo(i); }}
                      className={`transition-all duration-300 rounded-full ${
                        i === currentSlide
                          ? 'w-5 h-1.5 bg-accent'
                          : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/60'
                      }`}
                      aria-label={`Go to screenshot ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── THUMBNAIL STRIP ──────────────────────────────────────── */}
          {totalSlides > 1 && (
            <div className="flex gap-2 px-4 py-3 overflow-x-auto bg-[#020024]/60 scrollbar-thin scrollbar-thumb-accent/30 scrollbar-track-transparent">
              {slides.map((src, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); goTo(i); }}
                  className={`flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    i === currentSlide
                      ? 'border-accent shadow-[0_0_8px_rgba(52,183,209,0.5)]'
                      : 'border-white/10 opacity-50 hover:opacity-80 hover:border-white/30'
                  }`}
                >
                  <img
                    src={src}
                    alt={`Thumb ${i + 1}`}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          )}

          {/* ── PROJECT INFO ─────────────────────────────────────────── */}
          <div className="p-6 md:p-8">

            {/* Category + links row */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-sm font-semibold text-accent uppercase tracking-wider">
                {project.category}
              </span>
              <span className="text-white/30">•</span>
              <div className="flex gap-4">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-white/55 hover:text-accent transition-colors"
                  >
                    <Icon name="Globe" size={15} color="currentColor" />
                    {t('portfolio.liveDemo')}
                  </a>
                )}
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-white/55 hover:text-accent transition-colors"
                  >
                    <Icon name="Github" size={15} color="currentColor" />
                    {t('portfolio.sourceCode')}
                  </a>
                )}
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {project.title}
            </h2>

            <p className="text-white/65 mb-8 leading-relaxed text-sm md:text-base">
              {project.description}
            </p>

            {/* Challenge / Solution / Impact */}
            <div className="space-y-5 mb-8">
              {[
                { icon: 'AlertCircle', key: 'challenge', text: project.challenge },
                { icon: 'Lightbulb',  key: 'solution',  text: project.solution  },
                { icon: 'TrendingUp', key: 'impact',    text: project.impact    },
              ].map(({ icon, key, text }) => (
                <div key={key} className="glass rounded-xl p-4">
                  <h3 className="text-base font-bold text-white mb-2 flex items-center gap-2">
                    <Icon name={icon as any} size={17} color="var(--color-accent)" />
                    {t(`portfolio.${key}`)}
                  </h3>
                  <p className="text-white/60 leading-relaxed text-sm pl-6">{text}</p>
                </div>
              ))}
            </div>

            {/* Tech stack */}
            <div className="mb-8">
              <h3 className="text-base font-bold text-white mb-3">
                {t('portfolio.technologies')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-accent/15 text-accent border border-accent/20 rounded-lg text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA buttons */}
            <div className="flex gap-3 pt-6 border-t border-white/10">
              {project.liveUrl && (
                <Button
                  variant="default"
                  size="lg"
                  className="gradient-cta flex-1"
                  iconName="ExternalLink"
                  iconPosition="right"
                  iconSize={17}
                  onClick={() => window.open(project.liveUrl, '_blank')}
                >
                  {t('portfolio.liveDemo')}
                </Button>
              )}
              {project.githubUrl && (
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1 border-white/20 text-white hover:border-accent/50 hover:bg-accent/10"
                  iconName="Github"
                  iconPosition="right"
                  iconSize={17}
                  onClick={() => window.open(project.githubUrl, '_blank')}
                >
                  {t('portfolio.sourceCode')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;